'use client';

import { useState, useEffect, useRef } from 'react';
import { SearchIcon } from '@/assets/icons';
import { cn } from '@/lib/utils';
import { Select } from '@/components/FormElements/select';
import { getStays, getDestinations, Destination } from '@/lib/stays/api';

export interface StaySearchParams {
  destination?: string;
  checkIn?: string;
  checkOut?: string;
  rooms?: number;
  adults?: number;
  children?: number;
  nationality?: string;
}

interface StaysSearchFormProps {
  onSearch?: (params: StaySearchParams) => void;
  className?: string;
}

const ROOM_OPTIONS = [
  { value: '1', label: '1 Room' },
  { value: '2', label: '2 Rooms' },
  { value: '3', label: '3 Rooms' },
  { value: '4', label: '4 Rooms' },
  { value: '5', label: '5 Rooms' },
];

const NATIONALITY_OPTIONS = [
  { value: '', label: 'Select Nationality' },
  { value: 'US', label: 'United States' },
  { value: 'GB', label: 'United Kingdom' },
  { value: 'DE', label: 'Germany' },
  { value: 'FR', label: 'France' },
  { value: 'IT', label: 'Italy' },
  { value: 'ES', label: 'Spain' },
  { value: 'PT', label: 'Portugal' },
  { value: 'NL', label: 'Netherlands' },
  { value: 'BE', label: 'Belgium' },
  { value: 'CH', label: 'Switzerland' },
  { value: 'AT', label: 'Austria' },
  { value: 'AU', label: 'Australia' },
  { value: 'NZ', label: 'New Zealand' },
  { value: 'CA', label: 'Canada' },
  { value: 'JP', label: 'Japan' },
  { value: 'KR', label: 'South Korea' },
  { value: 'CN', label: 'China' },
  { value: 'TH', label: 'Thailand' },
  { value: 'SG', label: 'Singapore' },
  { value: 'MY', label: 'Malaysia' },
  { value: 'ID', label: 'Indonesia' },
  { value: 'VN', label: 'Vietnam' },
  { value: 'PH', label: 'Philippines' },
  { value: 'IN', label: 'India' },
  { value: 'AE', label: 'United Arab Emirates' },
  { value: 'SA', label: 'Saudi Arabia' },
  { value: 'BR', label: 'Brazil' },
  { value: 'AR', label: 'Argentina' },
  { value: 'MX', label: 'Mexico' },
  { value: 'RU', label: 'Russia' },
  { value: 'OTHER', label: 'Other' },
];

export default function StaysSearchForm({ onSearch, className }: StaysSearchFormProps) {
  const [destination, setDestination] = useState('');
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [rooms, setRooms] = useState('1');
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [nationality, setNationality] = useState('');

  // Autocomplete state
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [allDestinations, setAllDestinations] = useState<Destination[]>([]);
  const [destinationsLoaded, setDestinationsLoaded] = useState(false);
  const autocompleteRef = useRef<HTMLDivElement>(null);

  // Fetch all destinations once on mount
  useEffect(() => {
    const fetchDestinations = async () => {
      try {
        const destinations = await getDestinations();
        setAllDestinations(destinations);
        setDestinationsLoaded(true);
      } catch (error) {
        console.error('Error fetching destinations:', error);
        setDestinationsLoaded(true);
      }
    };
    fetchDestinations();
  }, []);

  // Close autocomplete when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (autocompleteRef.current && !autocompleteRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter destination suggestions client-side
  useEffect(() => {
    if (destination.length < 2) {
      setSuggestions([]);
      return;
    }

    const searchTerm = destination.toLowerCase();
    const filtered = allDestinations
      .filter((d) => d.city.toLowerCase().includes(searchTerm))
      .map((d) => d.country ? `${d.city}, ${d.country}` : d.city);
    
    // Remove duplicates
    const unique = [...new Set(filtered)];
    setSuggestions(unique);
  }, [destination, allDestinations]);

  const handleSuggestionClick = (suggestion: string) => {
    // Extract city name (before comma if country is shown)
    const city = suggestion.split(',')[0].trim();
    setDestination(city);
    setShowSuggestions(false);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate check-out is after check-in
    if (checkIn && checkOut && new Date(checkOut) <= new Date(checkIn)) {
      alert('Check-out date must be after check-in date');
      return;
    }

    const params: StaySearchParams = {};
    if (destination) params.destination = destination;
    if (checkIn) params.checkIn = checkIn;
    if (checkOut) params.checkOut = checkOut;
    if (rooms) params.rooms = parseInt(rooms);
    if (adults > 1) params.adults = adults;
    if (children > 0) params.children = children;
    if (nationality) params.nationality = nationality;

    onSearch?.(params);
  };

  const incrementCount = (type: 'adults' | 'children', max?: number) => {
    if (type === 'adults') {
      if (max === undefined || adults < max) setAdults((prev) => prev + 1);
    } else {
      if (max === undefined || children < max) setChildren((prev) => prev + 1);
    }
  };

  const decrementCount = (type: 'adults' | 'children') => {
    if (type === 'adults' && adults > 1) {
      setAdults((prev) => prev - 1);
    } else if (type === 'children' && children > 0) {
      setChildren((prev) => prev - 1);
    }
  };

  const handleReset = () => {
    setDestination('');
    setCheckIn('');
    setCheckOut('');
    setRooms('1');
    setAdults(1);
    setChildren(0);
    setNationality('');
    onSearch?.({});
  };

  // Set minimum check-in date to today
  const getMinCheckIn = () => {
    return new Date().toISOString().split('T')[0];
  };

  // Set minimum check-out date to check-in date
  const getMinCheckOut = () => {
    if (!checkIn) return getMinCheckIn();
    const nextDay = new Date(checkIn);
    nextDay.setDate(nextDay.getDate() + 1);
    return nextDay.toISOString().split('T')[0];
  };

  return (
    <div className={cn('rounded-[10px] bg-white shadow-1 dark:bg-gray-dark dark:shadow-card', className)}>
      <form onSubmit={handleSearch} className="p-4 sm:p-6 xl:p-10">
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {/* Destination Autocomplete */}
          <div className="relative" ref={autocompleteRef}>
            <label className="mb-3 block text-body-sm font-medium text-dark dark:text-white">
              Destination
            </label>
            <div className="relative">
              <input
                type="text"
                value={destination}
                onChange={(e) => setDestination(e.target.value)}
                onFocus={() => setShowSuggestions(true)}
                placeholder="Search city..."
                className="w-full rounded-lg border-[1.5px] border-stroke bg-transparent px-5 py-3 outline-none transition focus:border-primary active:border-primary dark:border-dark-3 dark:bg-dark-2 dark:focus:border-primary"
              />

              {/* Suggestions Dropdown */}
              {showSuggestions && (destination.length >= 2) && (
                <div className="absolute z-50 mt-1 w-full rounded-lg border border-stroke bg-white shadow-lg dark:border-dark-3 dark:bg-dark-2">
                  {!destinationsLoaded ? (
                    <div className="px-5 py-3 text-sm text-gray-500 dark:text-gray-400">
                      Loading destinations...
                    </div>
                  ) : suggestions.length > 0 ? (
                    <ul className="max-h-60 overflow-auto py-1">
                      {suggestions.map((suggestion, index) => (
                        <li
                          key={index}
                          onClick={() => handleSuggestionClick(suggestion)}
                          className="cursor-pointer px-5 py-2.5 text-sm hover:bg-gray-2 dark:hover:bg-dark dark:text-white"
                        >
                          {suggestion}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="px-5 py-3 text-sm text-gray-500 dark:text-gray-400">
                      No destinations found
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Check-in Date */}
          <div>
            <label className="mb-3 block text-body-sm font-medium text-dark dark:text-white">
              Check-in
            </label>
            <div className="relative">
              <input
                type="date"
                value={checkIn}
                onChange={(e) => {
                  setCheckIn(e.target.value);
                  // Reset check-out if it's before new check-in
                  if (checkOut && new Date(checkOut) <= new Date(e.target.value)) {
                    setCheckOut('');
                  }
                }}
                min={getMinCheckIn()}
                className="w-full rounded-lg border-[1.5px] border-stroke bg-transparent px-5 py-3 outline-none transition focus:border-primary active:border-primary dark:border-dark-3 dark:bg-dark-2 dark:focus:border-primary dark:text-white"
              />
            </div>
          </div>

          {/* Check-out Date */}
          <div>
            <label className="mb-3 block text-body-sm font-medium text-dark dark:text-white">
              Check-out
            </label>
            <div className="relative">
              <input
                type="date"
                value={checkOut}
                onChange={(e) => setCheckOut(e.target.value)}
                min={getMinCheckOut()}
                className="w-full rounded-lg border-[1.5px] border-stroke bg-transparent px-5 py-3 outline-none transition focus:border-primary active:border-primary dark:border-dark-3 dark:bg-dark-2 dark:focus:border-primary dark:text-white"
              />
            </div>
          </div>

          {/* Rooms */}
          <div>
            <Select
              label="Rooms"
              placeholder="Select rooms"
              items={ROOM_OPTIONS}
              value={rooms}
              onChange={setRooms}
            />
          </div>

          {/* Adults Counter */}
          <div>
            <label className="mb-3 block text-body-sm font-medium text-dark dark:text-white">
              Adults
            </label>
            <div className="flex items-center rounded-lg border-[1.5px] border-stroke dark:border-dark-3">
              <button
                type="button"
                onClick={() => decrementCount('adults')}
                disabled={adults <= 1}
                className="flex h-12 w-12 items-center justify-center text-dark hover:bg-gray-2 disabled:cursor-not-allowed disabled:opacity-50 dark:text-white dark:hover:bg-dark"
              >
                <svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                </svg>
              </button>
              <div className="flex flex-1 items-center justify-center">
                <svg className="mr-2 size-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <span className="text-center font-medium text-dark dark:text-white">
                  {adults}
                </span>
              </div>
              <button
                type="button"
                onClick={() => incrementCount('adults')}
                className="flex h-12 w-12 items-center justify-center text-dark hover:bg-gray-2 dark:text-white dark:hover:bg-dark"
              >
                <svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </button>
            </div>
          </div>

          {/* Children Counter */}
          <div>
            <label className="mb-3 block text-body-sm font-medium text-dark dark:text-white">
              Children
            </label>
            <div className="flex items-center rounded-lg border-[1.5px] border-stroke dark:border-dark-3">
              <button
                type="button"
                onClick={() => decrementCount('children')}
                disabled={children <= 0}
                className="flex h-12 w-12 items-center justify-center text-dark hover:bg-gray-2 disabled:cursor-not-allowed disabled:opacity-50 dark:text-white dark:hover:bg-dark"
              >
                <svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                </svg>
              </button>
              <div className="flex flex-1 items-center justify-center">
                <svg className="mr-2 size-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <span className="text-center font-medium text-dark dark:text-white">
                  {children}
                </span>
              </div>
              <button
                type="button"
                onClick={() => incrementCount('children')}
                className="flex h-12 w-12 items-center justify-center text-dark hover:bg-gray-2 dark:text-white dark:hover:bg-dark"
              >
                <svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </button>
            </div>
          </div>

          {/* Nationality */}
          <div className="md:col-span-2 lg:col-span-1">
            <Select
              label="Nationality"
              placeholder="Select nationality"
              items={NATIONALITY_OPTIONS}
              value={nationality}
              onChange={setNationality}
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <button
            type="submit"
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-6 py-3 font-medium text-white hover:bg-opacity-90 transition duration-200 ease-in-out"
          >
            <SearchIcon className="size-5" />
            Search
          </button>
          <button
            type="button"
            onClick={handleReset}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-stroke px-6 py-3 font-medium text-dark hover:bg-gray-2 dark:border-dark-3 dark:text-white dark:hover:bg-dark transition duration-200 ease-in-out"
          >
            Reset
          </button>
        </div>
      </form>
    </div>
  );
}
